import React from 'react';
import { StyleSheet, Text, Pressable, View, Image } from 'react-native';

import { COLOR, FONT, FONT_SIZE, THEME, __WEB__ } from '../../constants/styles';
import { EVENT_TYPE_AUDIOGRAM } from '../../constants/eventTypes';

import { bindActionCreators } from 'redux';
import { connect } from 'react-redux';

import { getCurrentCasePatient, getCurrentCaseSupervisor } from '../cases/selector';
import { getCurrentCaseCurrentAudiogram } from '../patient/selector';
import { openCaseInfo, resetState } from '../cases/actions';
import { getPatientRequirementsForDepartment } from '../patient/actions';
import { getRequiredAndCompleteImmunesForCurrentCasePatient } from './selector';
import { getAssociatedEmployer } from '../../selectors/employers';

import { OPComponent, Container, I18n, Utils, DateUtils, Navigation } from '../components';

import Events from '../success/events';
import Immunizations from '../patient/airforce/immunizations';
import DisplayParticipant from './display_participant';
import OccHealthSummary from '../patient/airforce/occ_health_checklist';

import { currentDeploymentInfoForPatient } from './helpers';
import {
  ACTIVE_DUTY,
  RESERVE_DUTY,
  BENEFICIARY,
  CIVILIAN_STAFF,
  RETIREE,
} from '../../constants/patientRoles';
import GreetingHeader from '../employer/greeting_header';

const translate = __WEB__ ? I18n.c1 : I18n.ac;

class AirmanHome extends OPComponent {
  componentDidMount() {
    if (this.props.patient?.departmentId && this.props.employer) {
      this.props.actions.getPatientRequirementsForDepartment({
        employerId: this.props.employer?.employerId,
        departmentId: this.props.patient?.departmentId,
      });
    }
  }
  componentDidUpdate(prevProps) {
    if (this.props.patient?.departmentId && this.props.employer) {
      if (this.props.employer?.employerId !== prevProps.employer?.employerId) {
        this.props.actions.getPatientRequirementsForDepartment({
          employerId: this.props.employer?.employerId,
          departmentId: this.props.patient?.departmentId,
        });
      }
    }
  }
  editSupervisor = () => {
    this.props.actions.openCaseInfo(this.props.currentCaseId);
    Navigation.showModal({
      screen: 'SupervisorSelect',
      title: 'Select Supervisor',
      passProps: {
        callOnUnmount: this.props.actions.resetState,
      },
    });
  };

  onScheduleOccHealth = () => {
    const caseId = this.props.currentCaseObj.caseId;
    Navigation.showModal(
      {
        screen: 'PatientSchedule',
        title: '',
        passProps: {
          eventType: EVENT_TYPE_AUDIOGRAM,
          caseId,
        },
      },
      this.props.componentId
    );
  };
  renderOccHealth() {
    const props = this.props;
    const dueDates = props.patient?.extraData?.dueDates || {};
    const visible = [ACTIVE_DUTY, RESERVE_DUTY, CIVILIAN_STAFF].includes(props.patient.role);

    if (!visible) return null;
    return (
      <View style={[styles.panel, __WEB__ ? { width: 500 } : null]}>
        <View style={styles.panelHeader}>
          <Text style={styles.panelHeaderLabel}>
            {translate('airforce.occupationalHealth')} Summary
          </Text>
        </View>
        <OccHealthSummary
          patient={props.patient}
          editingCase={props.currentCaseObj}
          canEdit={false}
          panelView={true}
          dueDates={dueDates}
          deptReqsData={props.deptReqsData}
          currentAudiogramEvents={props.currentAudiogramEvents}
          onSchedule={this.onScheduleOccHealth}
        />
      </View>
    );
  }

  renderReadiness() {
    const props = this.props;

    const immuneCounts = {
      required: props.requiredAndCompleteImmunes?.required?.length || 0,
      complete: props.requiredAndCompleteImmunes?.complete?.length || 0,
    };
    const percentComplete = immuneCounts.required
      ? immuneCounts.complete / immuneCounts.required
      : null;
    // const percentComplete = .8;
    const intComplete = Math.round(percentComplete * 100);

    const deploymentInfo = currentDeploymentInfoForPatient(props.patient);
    const readinessDate = deploymentInfo?.date;
    const isReady = !!deploymentInfo?.ready?.clearanceGranted;

    const readyColor = !!isReady ? COLOR.success : COLOR.warn;
    const clearedText = isReady ? I18n.t('airforce.cleared') : I18n.t('airforce.notCleared');
    const visible = [ACTIVE_DUTY, RESERVE_DUTY].includes(props.patient.role);

    if (!visible) return null;
    return (
      <View style={styles.panel}>
        <View style={styles.panelHeader}>
          <Text style={styles.panelHeaderLabel}>{translate('airforce.readinessStatus')}</Text>
        </View>

        {!!readinessDate && (
          <View>
            <View
              style={[
                {
                  flexDirection: 'row',
                  borderBottomWidth: 1,
                  borderColor: '#e4e4e4',
                },
              ]}
            >
              <View style={[styles.readinessDateContainer, styles.panelContent]}>
                <Text style={styles.readinessDateText}>
                  Readiness Date: {DateUtils.formatDateString(readinessDate)}
                </Text>
              </View>
              <View style={[styles.panelContent]}>
                <Text style={styles.percentCompleteText}>{intComplete}%</Text>
                <Text style={styles.immText}>Immunized</Text>
                <Text style={styles.subText}>
                  #{immuneCounts.complete} of {immuneCounts.required}
                </Text>
              </View>
            </View>
            <View style={[THEME.basicRow, styles.panelContent]}>
              <View style={[styles.clearanceIndicator, { backgroundColor: readyColor }]} />
              <Text style={THEME.textRegular}>{clearedText}</Text>
            </View>
          </View>
        )}

        {!readinessDate && (
          <View style={styles.panelContent}>
            <Text style={styles.phaCompleteText}>No Upcoming Readiness</Text>
          </View>
        )}
      </View>
    );
  }

  renderImmunizations() {
    return (
      <View style={styles.panel}>
        <View style={styles.panelHeader}>
          <Text style={styles.panelHeaderLabel}>{translate('airforce.immunizations')}</Text>
        </View>
        <Immunizations
          forCurrentCase={true}
          scrollable={false}
          rowContainerStyle={styles.immunizationsContainer}
        />
      </View>
    );
  }

  renderPhaStatus() {
    const props = this.props;

    const phaInfo = props.patient?.extraData?.pha;
    const phaDueDate = phaInfo?.dueDate;
    const isComplete = !!phaInfo?.approvedDate;

    const readyColor = !!isComplete ? COLOR.success : COLOR.warn;
    const clearedText = isComplete ? I18n.ce('completed') : I18n.ce('notCompleted');
    const visible = [ACTIVE_DUTY, RESERVE_DUTY].includes(props.patient.role);

    if (!visible) return null;
    return (
      <View style={styles.panel}>
        <View style={styles.panelHeader}>
          <Text style={styles.panelHeaderLabel}>{translate('airforce.phaStatus')}</Text>
        </View>

        {!!phaDueDate && (
          <View style={[THEME.basicRow, styles.panelContent]}>
            <View style={[styles.clearanceIndicator, { backgroundColor: readyColor }]} />
            <Text style={styles.phaCompleteText}>{clearedText}</Text>

            <Text style={styles.phaDateText}>Due: {DateUtils.formatDateString(phaDueDate)}</Text>
          </View>
        )}

        {!phaDueDate && (
          <View style={styles.panelContent}>
            <Text style={styles.phaCompleteText}>No Upcoming PHA</Text>
          </View>
        )}
      </View>
    );
  }

  renderSupervisor() {
    const supervisor = this.props.supervisor;
    if (!supervisor) return null;
    return (
      <View style={styles.panel}>
        <View style={[styles.panelHeader, THEME.basicRow]}>
          <Text style={[styles.panelHeaderLabel, { flex: 1 }]}>Supervisor</Text>
          <Pressable onPress={this.editSupervisor}>
            <Text style={THEME.linkStyle}>Edit</Text>
          </Pressable>
        </View>
        <View style={styles.panelContent}>
          <DisplayParticipant participant={supervisor} />
        </View>
      </View>
    );
  }

  render() {
    const props = this.props;

    return (
      <Container scrollView={true}>
        <GreetingHeader
          group={props.currentCaseObj?.group}
          employer={props.employer}
          userProfile={props.userProfile}
          applyPadding={true}
        />
        <View style={styles.contentContainerStyle}>
          {this.renderReadiness()}

          {this.renderOccHealth()}

          {this.renderPhaStatus()}

          {this.renderImmunizations()}

          <Events
            screenVisible={props.screenVisible}
            currentCaseObj={props.currentCaseObj}
            containerStyle={styles.panel}
            disableDidItEditing={true}
          />

          {this.renderSupervisor()}
        </View>
      </Container>
    );
  }
}

const styles = StyleSheet.create({
  contentContainerStyle: __WEB__
    ? {
        flexDirection: 'row',
        flexWrap: 'wrap',
        borderTopWidth: 1,
        borderColor: '#e4e4e4',
      }
    : {},
  panel: __WEB__
    ? {
        width: 400,
        borderWidth: 1,
        borderColor: COLOR.altAccent,
        shadowColor: COLOR.overlay,
        shadowOffset: { width: 0, height: 0 },
        shadowRadius: 10,
        margin: 10,
      }
    : {},
  immunizationsContainer: {
    backgroundColor: COLOR.contrast,
    alignItems: 'center',
  },
  percentCompleteText: {
    ...FONT.regular,
    fontSize: 24,
    color: COLOR.emphasis,
    textAlign: 'center',
  },
  immText: {
    ...THEME.textRegular,
    textAlign: 'center',
  },
  subText: {
    ...FONT.regular,
    fontSize: 12,
    color: COLOR.text,
    textAlign: 'center',
  },
  panelHeader: {
    height: 50,
    justifyContent: 'center',
    backgroundColor: COLOR.altContrast,
    paddingHorizontal: 30,
    borderBottomWidth: 1,
    borderColor: '#e4e4e4',
  },
  panelHeaderLabel: __WEB__
    ? {
        ...FONT.semibold,
        fontSize: 12,
        color: COLOR.text,
      }
    : {
        ...FONT.medium,
        fontSize: 14,
        color: COLOR.textLight,
      },
  panelContent: {
    padding: 25,
  },
  readinessDateContainer: {
    flex: 1,
    borderRightWidth: 1,
    borderColor: '#e4e4e4',
  },
  readinessDateText: {
    ...FONT.bold,
    fontSize: 14,
    color: COLOR.text,
  },
  clearanceIndicator: {
    width: 20,
    height: 20,
    borderRadius: 20,
    marginRight: 20,
  },
  phaCompleteText: {
    flex: 1,
    ...FONT.regular,
    color: COLOR.textLight,
    fontSize: 14,
  },
  phaDateText: {
    ...FONT.regular,
    color: COLOR.textLight,
    fontSize: 14,
  },
});

function mapStateToProps(state) {
  return {
    patient: getCurrentCasePatient(state),
    requiredAndCompleteImmunes: getRequiredAndCompleteImmunesForCurrentCasePatient(state),
    supervisor: getCurrentCaseSupervisor(state),
    currentCaseId: state.application.currentCaseId,
    userProfile: state.entities.userProfile,
    employer: getAssociatedEmployer(state),
    deptReqsData: state.screens.patients.deptReqsData,
    currentAudiogramEvents: getCurrentCaseCurrentAudiogram(state),
  };
}

function mapDispatchToProps(dispatch) {
  return {
    actions: bindActionCreators(
      {
        openCaseInfo,
        resetState,
        getPatientRequirementsForDepartment,
      },
      dispatch
    ),
  };
}

export default connect(mapStateToProps, mapDispatchToProps)(AirmanHome);
